"use client";

import { useCallback, useEffect, useState } from "react";
import OtpContent from "./OtpContent";
import useOtpCountdown from "@/hooks/auth/useOtpCountdown";
import { useAuthModal } from "@/hooks/auth/useAuthModal";
import { SendOtpParamSchema, VerifyOtpParamSchema } from "@/types/auth";
import { useSendOtp, useVerifyOtp } from "@/hooks/auth/useAuth";

const OTP_LENGTH = 6;
const OTP_RESEND_EXPIRY_MS = 120 * 1000;

const getResendPayload = (
  flow: string | null,
  method: "phone" | "email",
  phone: string | null,
  email: string | null,
) => {
  return SendOtpParamSchema.parse({
    ...(method === "phone" && phone
      ? { countryCode: "855", phone: phone.replace(/\D/g, "") }
      : {}),
    ...(method === "email" && email ? { email } : {}),
    type: flow === "forgot" ? "forgot" : "register",
    method,
  });
};

const getVerifyPayload = (
  otp: string,
  verificationToken: string,
  method: "phone" | "email",
) => {
  return VerifyOtpParamSchema.parse({
    otp,
    verificationToken,
    method,
  });
};

const OtpStep = () => {
  const {
    phone,
    email,
    authMethod,
    verificationToken,
    setStep,
    setVerificationToken,
  } = useAuthModal();

  const [otpValue, setOtpValue] = useState("");
  const [otpResetKey, setOtpResetKey] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const { resendCountdown, startCountdown } = useOtpCountdown();

  // Guard: if verification token is missing, redirect to previous entry step
  useEffect(() => {
    if (verificationToken) return;

    const previousStep =
      sessionStorage.getItem("auth_flow") === "forgot"
        ? "forgot-password"
        : "register";

    void setStep(previousStep);
  }, [setStep, verificationToken]);

  const { mutate: sendOtp, isPending: isSendingOtp } = useSendOtp({
    onSuccess: (response) => {
      const responseData = response.data as
        | { verificationToken?: string }
        | undefined;

      if (responseData?.verificationToken) {
        void setVerificationToken(responseData.verificationToken);
      }

      sessionStorage.setItem(
        "otp_resend_expiry",
        (Date.now() + OTP_RESEND_EXPIRY_MS).toString(),
      );
      setOtpValue("");
      setOtpResetKey((prev) => prev + 1);
      startCountdown();
    },
    onError: (message) => {
      setErrorMessage(message);
    },
  });

  const { mutate: verifyOtp, isPending: isVerifyingOtp } = useVerifyOtp({
    onSuccess: (response) => {
      const responseData = response.data as
        | { loginToken?: string }
        | undefined;

      if (responseData?.loginToken) {
        void setVerificationToken(responseData.loginToken);
      }

      const authFlow = sessionStorage.getItem("auth_flow");
      void setStep(authFlow === "forgot" ? "reset-password" : "create-password");
    },
    onError: (message) => {
      setErrorMessage(message);
    },
  });

  const handleOtpChange = useCallback((value: string) => {
    setErrorMessage("");
    setOtpValue(value);
  }, []);

  const handleResend = useCallback(() => {
    const flow = sessionStorage.getItem("auth_flow");
    const resendMethod = (flow === "forgot" ? authMethod || "phone" : "phone") as "phone" | "email";

    if (isSendingOtp) return;
    if (resendMethod === "phone" && !phone) return;
    if (resendMethod === "email" && !email) return;

    setErrorMessage("");

    try {
      const payload = getResendPayload(flow, resendMethod, phone, email);
      sendOtp(payload);
    } catch {
      setErrorMessage("Failed to send OTP. Please check your details.");
    }
  }, [authMethod, email, isSendingOtp, phone, sendOtp]);

  const handleVerify = useCallback(
    (event?: React.BaseSyntheticEvent) => {
      event?.preventDefault();

      if (!verificationToken || otpValue.length !== OTP_LENGTH || isVerifyingOtp) {
        return;
      }

      setErrorMessage("");

      try {
        const payload = getVerifyPayload(
          otpValue,
          verificationToken,
          (authMethod || "phone") as "phone" | "email",
        );
        verifyOtp(payload);
      } catch {
        setErrorMessage("Invalid OTP format. Please enter 6 digits.");
      }
    },
    [authMethod, isVerifyingOtp, otpValue, verificationToken, verifyOtp],
  );

  return (
    <OtpContent
      otpResetKey={otpResetKey}
      otpValue={otpValue}
      onOtpChange={handleOtpChange}
      onSubmit={handleVerify}
      resendCountdown={resendCountdown}
      onResendClick={handleResend}
      isSubmitting={isVerifyingOtp}
      isResending={isSendingOtp}
      errorMessage={errorMessage}
    />
  );
};

export default OtpStep;
